import { Header, renderServiceTabs, AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../components';
import { setState, navigateTo } from '../store';
import { getSpaConfig } from '../api';
import { getCurrentLanguage, formatLocalDate, t } from '../i18n';
import { renderCarousel } from '../components/carousel';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

interface LocalizedString { th: string; en: string; zh: string; ru?: string; ja?: string; ko?: string; }
interface Treatment {
  code: string;
  name: LocalizedString | string;
  nameTh?: string;
  nameEn?: string;
  nameZh?: string;
  description?: LocalizedString | string;
  desc?: string;
  duration: number;
  price: number;
}

interface OutletImage {
  url: string;
  thumbUrl?: string;
}

interface SpaOutletData {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  openTime?: string;
  closeTime?: string;
  slotIntervalMinutes?: number;
  maxBedsPerSlot?: number;
  images?: OutletImage[];
  treatments?: Treatment[];
  outlets?: SpaOutletData[];
}

let spaOutlets: SpaOutletData[] = [];
let activeOutletId: string | null = null;
let spaConfigLoaded = false;

let TREATMENTS: Treatment[] = [
  { 
    code: 'AROMA_60', 
    name: { 
      th: 'นวดอโรม่าเทอราพี (Aromatherapy Massage)', 
      en: 'Aromatherapy Massage (60 min)', 
      zh: '芳疗按摩 (60分钟)',
      ru: 'Ароматерапевтический массаж (60 мин)',
      ja: 'アロマセラピーマッサージ (60分)',
      ko: '아로마 테라피 마사지 (60분)'
    }, 
    duration: 60, 
    price: 1500, 
    desc: 'ผ่อนคลายด้วยน้ำมันหอมระเหยสูตรพิเศษ' 
  },
  { 
    code: 'THAI_90',  
    name: { 
      th: 'นวดแผนไทยโบราณ (Traditional Thai Massage)', 
      en: 'Traditional Thai Massage (90 min)', 
      zh: '泰式按摩 (90分钟)',
      ru: 'Традиционный тайский массаж (90 мин)',
      ja: 'タイ古式マッサージ (90分)',
      ko: '전통 태국 마사지 (90분)'
    }, 
    duration: 90, 
    price: 1800, 
    desc: 'นวดกดจุดผ่อนคลายกล้ามเนื้อทั่วร่างกาย' 
  },
  { 
    code: 'FOOT_60',  
    name: { 
      th: 'นวดกดจุดฝ่าเท้า (Foot Reflexology)', 
      en: 'Foot Reflexology (60 min)', 
      zh: '足部按摩 (60分钟)',
      ru: 'Массаж стоп (60 мин)',
      ja: 'フットリフレクソロジー (60分)',
      ko: '발 반사 요법 마사지 (60분)'
    }, 
    duration: 60, 
    price: 1000, 
    desc: 'นวดกดจุดสะท้อนเท้าเพื่อสุขภาพ' 
  },
];

const TIME_SLOTS = ['10:00', '11:30', '13:00', '14:00', '15:30', '17:00', '18:30'];

let selectedTreatmentCode = 'AROMA_60';
let selectedDateStr = formatLocalDate();
let selectedTimeSlot = '14:00';
let paxCount = 2;
let roomNumberInput = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.en || val.th || val.zh || val.ru || val.ja || val.ko || fallback;
  }
  return fallback;
}

export function renderSpaPage(): string {
  const lang = getCurrentLanguage();

  const editBookingId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingId') : null;
  let editBookingData: any = null;
  if (editBookingId) {
    try {
      editBookingData = JSON.parse(sessionStorage.getItem('editBookingData') || '{}');
      const spa = editBookingData.spaDetails || editBookingData.customFields?.spaDetails;
      if (spa) {
        if (spa.bookingDate) selectedDateStr = spa.bookingDate;
        if (spa.timeSlot) selectedTimeSlot = spa.timeSlot;
        if (spa.treatmentCode) selectedTreatmentCode = spa.treatmentCode;
        if (spa.pax) paxCount = spa.pax;
        if (spa.roomNumber) roomNumberInput = spa.roomNumber;
        if (spa.outletId && spaOutlets.some(o => o.id === spa.outletId)) activeOutletId = spa.outletId;
      }
    } catch (_) {}
  }

  const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
  let currentTreatments = [...(activeOutlet.treatments || TREATMENTS)];

  // Ensure selected treatment exists in current treatments list
  if (selectedTreatmentCode && !currentTreatments.some(t => t.code === selectedTreatmentCode)) {
    const customTreatName = editBookingData?.spaDetails?.treatmentName || editBookingData?.customFields?.spaDetails?.treatmentName || selectedTreatmentCode;
    currentTreatments.push({
      code: selectedTreatmentCode,
      name: typeof customTreatName === 'object' ? customTreatName : { th: customTreatName, en: customTreatName, zh: customTreatName },
      duration: editBookingData?.spaDetails?.durationMinutes || 60,
      price: (editBookingData?.totalAmount || 0) / (paxCount || 1) || 1000,
      desc: '',
    });
  }

  const outletImages = activeOutlet.images || [];

  // Ensure time slot list includes selectedTimeSlot
  const availableSlots = [...TIME_SLOTS];
  if (selectedTimeSlot && !availableSlots.includes(selectedTimeSlot)) {
    availableSlots.push(selectedTimeSlot);
    availableSlots.sort();
  }

  const selectedTreat = currentTreatments.find(t => t.code === selectedTreatmentCode) || currentTreatments[0] || TREATMENTS[0];
  const totalPrice = (selectedTreat.price || 1000) * paxCount;
  const spaNameText = getLocalizedText(activeOutlet.name, lang, 'สปา (Spa)');
  const spaDescText = getLocalizedText(activeOutlet.description, lang, '');
  const openHoursText = activeOutlet.openTime && activeOutlet.closeTime ? `${activeOutlet.openTime} – ${activeOutlet.closeTime}` : '09:00 – 21:00';

  const uiText: Record<string, {
    dateTitle: string;
    treatTitle: string;
    paxTitle: string;
    paxSub: string;
    roomLabel: string;
    roomPlace: string;
    totalPriceLabel: string;
    nextBtn: string;
    openHours: string;
    mins: string;
  }> = {
    th: {
      dateTitle: '📅 เลือกรอบวันและเวลา',
      treatTitle: '💆 เลือกทรีตเมนต์สปา',
      paxTitle: '👥 จำนวนผู้รับบริการ (Guests)',
      paxSub: 'จำนวนผู้รับบริการนวด',
      roomLabel: '🏨 หมายเลขห้องพัก (กรณีเป็นแขกพักในโรงแรม - Optional)',
      roomPlace: 'ระบุหมายเลขห้อง เช่น 1002',
      totalPriceLabel: 'ราคารวม (ชำระเงินที่ร้าน)',
      nextBtn: 'ถัดไป: กรอกข้อมูลผู้ใช้บริการ ›',
      openHours: '🕒 เวลาทำการ',
      mins: 'นาที',
    },
    en: {
      dateTitle: '📅 Select Date & Time',
      treatTitle: '💆 Select Spa Treatment',
      paxTitle: '👥 Number of Guests',
      paxSub: 'Number of guests for massage',
      roomLabel: '🏨 Room Number (For In-house Guests - Optional)',
      roomPlace: 'e.g. Room 1002',
      totalPriceLabel: 'Total Price (Pay at Spa)',
      nextBtn: 'Next: Guest Details ›',
      openHours: '🕒 Operating Hours',
      mins: 'mins',
    },
    zh: {
      dateTitle: '📅 选择日期与时间',
      treatTitle: '💆 选择水疗项目',
      paxTitle: '👥 客人人数',
      paxSub: '按摩服务人数',
      roomLabel: '🏨 房间号 (店内住客 - 可选)',
      roomPlace: '例如: 1002',
      totalPriceLabel: '总价 (到店支付)',
      nextBtn: '下一步：填写客人信息 ›',
      openHours: '🕒 营业时间',
      mins: '分钟',
    },
    ru: {
      dateTitle: '📅 Выберите дату и время',
      treatTitle: '💆 Выберите спа-процедуру',
      paxTitle: '👥 Количество гостей',
      paxSub: 'Количество гостей на процедуру',
      roomLabel: '🏨 Номер комнаты (для гостей отеля - не обязательно)',
      roomPlace: 'напр. 1002',
      totalPriceLabel: 'Итого (оплата на месте)',
      nextBtn: 'Далее: Данные гостя ›',
      openHours: '🕒 Часы работы',
      mins: 'мин',
    },
    ja: {
      dateTitle: '📅 日時を選択',
      treatTitle: '💆 スパトリートメントを選択',
      paxTitle: '👥 ご利用人数',
      paxSub: '施術を受けられる人数',
      roomLabel: '🏨 部屋番号 (ご宿泊のお客様 - 任意)',
      roomPlace: '例: 1002',
      totalPriceLabel: '合計金額 (現地でお支払い)',
      nextBtn: '次へ: お客様情報の入力 ›',
      openHours: '🕒 営業時間',
      mins: '分',
    },
    ko: {
      dateTitle: '📅 날짜 및 시간 선택',
      treatTitle: '💆 스파 트리트먼트 선택',
      paxTitle: '👥 이용 인원',
      paxSub: '마사지 이용 인원',
      roomLabel: '🏨 객실 번호 (투숙객 전용 - 선택사항)',
      roomPlace: '예: 1002호',
      totalPriceLabel: '총 결제 금액 (현장 결제)',
      nextBtn: '다음: 예약자 정보 입력 ›',
      openHours: '🕒 운영 시간',
      mins: '분',
    },
  };
  const currentUiText = uiText[lang] || uiText.en;

  return `
    ${Header(spaNameText, false, true)}
    ${renderServiceTabs('spa')}

    <div class="page-content" id="spa-content" style="padding:16px;padding-bottom:100px;">

      ${editBookingId ? `
        <div style="margin-bottom: 12px; padding: 10px 14px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 6px; font-size: 13px; color: #856404; display: flex; justify-content: space-between; align-items: center;">
          <div><strong>✏️ ${t('editModeBanner')}</strong> (${editBookingData?.confirmationNumber || editBookingId})</div>
          <button type="button" onclick="sessionStorage.removeItem('editBookingId');sessionStorage.removeItem('editBookingData');window.location.reload();" style="border:none;background:none;color:#dc3545;font-size:12px;cursor:pointer;text-decoration:underline;">${t('cancelEdit')}</button>
        </div>
      ` : ''}

      <!-- Outlet Selector Tabs (matching Rooms & MICE visual rhythm) -->
      <div id="spa-outlets-scroll" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;touch-action:pan-x;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;-ms-overflow-style:none;">
        ${(spaOutlets.length > 0 ? spaOutlets : [{ id: activeOutletId || 'spa', name: spaNameText }]).map((o) => {
          const isSelected = !activeOutletId || activeOutletId === o.id || spaOutlets.length === 1;
          return `
            <button
              id="spa-outlet-tab-${o.id}"
              type="button"
              ${spaOutlets.length > 1 ? `onclick="window.selectSpaOutlet?.('${o.id}')"` : ''}
              style="flex:0 0 auto;padding:8px 16px;border-radius:20px;border:1.5px solid ${isSelected ? '#00B900' : '#e0e0e0'};background:${isSelected ? '#00B900' : '#fff'};color:${isSelected ? '#fff' : '#333'};font-size:13px;font-weight:600;white-space:nowrap;cursor:${spaOutlets.length > 1 ? 'pointer' : 'default'};touch-action:manipulation;transition:all 0.15s ease;"
            >
              🌿 ${getLocalizedText(o.name, lang, spaNameText || 'Spa')}
            </button>
          `;
        }).join('')}
      </div>

      <!-- Split 2-Column Layout on Desktop -->
      <div class="service-detail-layout">
        <!-- Left Column: Showcase & Treatments -->
        <div class="service-left-col">
          <!-- Cover Image Banner Carousel Slider & Outlet Details -->
          <div style="background:#fff;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:0;">
            <div style="padding:8px 8px 0 8px;">
              <div class="room-images" id="spa-images-container" style="position:relative;border-radius:10px;overflow:hidden;height:220px;margin:0;">
                ${outletImages.length === 0 ? `
                  <div style="width:100%;height:220px;background:linear-gradient(135deg, #00B900 0%, #008a00 100%);display:flex;align-items:center;justify-content:center;color:#fff;font-size:36px;">
                    🌿
                  </div>
                ` : ''}
              </div>
            </div>

            <div style="padding:12px 14px 14px 14px;">
              <h2 style="font-size:18px;font-weight:700;color:#1a1a1a;margin-bottom:4px;">${spaNameText}</h2>
              ${spaDescText ? `<p style="font-size:13px;color:#666;line-height:1.4;margin-bottom:10px;">${spaDescText}</p>` : ''}
              <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#E8F5E8;color:#00B900;border-radius:12px;font-size:12px;font-weight:600;">
                  ${currentUiText.openHours}: ${openHoursText}
                </span>
                ${activeOutlet.slotIntervalMinutes ? `
                  <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#f5f5f5;color:#666;border-radius:12px;font-size:12px;">
                    ⏱️ ${activeOutlet.slotIntervalMinutes} ${currentUiText.mins}/slot
                  </span>
                ` : ''}
              </div>
            </div>
          </div>

          <!-- Treatment Selector Card -->
          <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
            <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${currentUiText.treatTitle}</h3>
            <div style="display:flex;flex-direction:column;gap:12px;">
              ${currentTreatments.map(t => {
                const treatName = getLocalizedText(t.name, lang, t.nameTh || t.code);
                const treatDesc = getLocalizedText(t.description || t.desc, lang, typeof t.desc === 'string' ? t.desc : '');
                const isSelected = selectedTreatmentCode === t.code;
                return `
                  <div 
                    id="spa-treat-card-${t.code}"
                    class="spa-treat-item"
                    data-code="${t.code}"
                    data-price="${t.price || 0}"
                    onclick="window.selectSpaTreatment?.('${t.code}')"
                    style="display:flex;align-items:flex-start;gap:12px;padding:12px;border-radius:10px;border:1.5px solid ${isSelected ? '#00B900' : '#e0e0e0'};background:${isSelected ? '#F9FDF9' : '#fff'};cursor:pointer;transition:all 0.15s ease;"
                  >
                    <input type="radio" id="spa-treat-radio-${t.code}" name="spa_treatment" ${isSelected ? 'checked' : ''} style="accent-color:#00B900;width:18px;height:18px;margin-top:2px;pointer-events:none;">
                    <div style="flex:1;">
                      <div style="font-weight:600;font-size:14px;color:#1a1a1a;">${treatName}</div>
                      ${treatDesc ? `<div style="font-size:12px;color:#666;line-height:1.4;margin-top:3px;">${treatDesc}</div>` : ''}
                      <div style="font-size:11px;color:#888;margin-top:4px;font-weight:500;">⏱️ ${t.duration} ${currentUiText.mins}</div>
                    </div>
                    <div style="font-weight:700;font-size:15px;color:#00B900;white-space:nowrap;margin-top:2px;">฿${(t.price || 0).toLocaleString()}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </div>

        <!-- Right Column: Booking Controls (Sticky on Desktop) -->
        <div class="service-right-col">
          <!-- Date & Time Picker Card -->
          <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
            <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${currentUiText.dateTitle}</h3>

            <!-- Date Picker (Litepicker single date) -->
            <div class="date-section-vertical" style="margin-bottom:12px;">
              <div id="spa-date-range-trigger" class="date-range-display" style="cursor:pointer;display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border:1.5px solid #e0e0e0;border-radius:10px;background:#fff;">
                <div style="display:flex;align-items:center;gap:10px;">
                  <span style="font-size:18px;">📅</span>
                  <div>
                    <div style="font-size:11px;color:#888;font-weight:500;">${lang === 'th' ? 'วันที่' : lang === 'zh' ? '日期' : lang === 'ru' ? 'Дата' : lang === 'ja' ? '日付' : lang === 'ko' ? '날짜' : 'Date'}</div>
                    <div id="spa-date-display" style="font-size:15px;font-weight:700;color:#1a1a1a;">${(() => { try { const d = new Date(selectedDateStr + 'T00:00:00'); return d.toLocaleDateString(lang === 'th' ? 'th-TH' : lang === 'zh' ? 'zh-CN' : lang === 'ru' ? 'ru-RU' : lang === 'ja' ? 'ja-JP' : lang === 'ko' ? 'ko-KR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch(_) { return selectedDateStr; } })()}</div>
                  </div>
                </div>
                <span id="spa-date-toggle-icon" style="color:#00B900;font-size:16px;transition:transform 0.2s;">▼</span>
              </div>
              <!-- Litepicker inline container (collapsed by default) -->
              <div id="spa-litepicker-container" class="litepicker-inline collapsed"></div>
              <input type="hidden" id="spa-date-hidden" value="${selectedDateStr}" />
            </div>

            <!-- Time Slot Chips -->
            <div style="display:flex;flex-wrap:wrap;gap:8px;">
              ${availableSlots.map(slot => `
                <button 
                  id="spa-time-chip-${slot.replace(':', '_')}"
                  class="spa-time-chip time-chip" 
                  onclick="window.selectSpaTimeSlot?.('${slot}')"
                  style="padding:8px 14px;border-radius:20px;border:1px solid ${selectedTimeSlot === slot ? '#00B900' : '#e0e0e0'};background:${selectedTimeSlot === slot ? '#00B900' : '#fff'};color:${selectedTimeSlot === slot ? '#fff' : '#333'};font-size:13px;font-weight:600;cursor:pointer;transition:all 0.15s ease;"
                >
                  ${slot}
                </button>
              `).join('')}
            </div>
          </div>

          <!-- Guest Count & Room Number Card -->
          <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
              <div>
                <div style="font-size:15px;font-weight:600;color:#1a1a1a;">${currentUiText.paxTitle}</div>
                <div style="font-size:12px;color:#666;">${currentUiText.paxSub}</div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;border:1px solid #e0e0e0;border-radius:8px;padding:4px 8px;">
                <button onclick="window.changeSpaPax?.(-1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">-</button>
                <span id="spa-pax-display" style="font-size:15px;font-weight:600;min-width:20px;text-align:center;">${paxCount}</span>
                <button onclick="window.changeSpaPax?.(1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">+</button>
              </div>
            </div>

            <div style="border-top:1px solid #f0f0f0;padding-top:12px;">
              <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${currentUiText.roomLabel}</label>
              <input 
                type="text" 
                id="spa_room_number" 
                value="${roomNumberInput}"
                onchange="window.updateSpaRoomNumber?.(this.value)"
                placeholder="${currentUiText.roomPlace}"
                style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;"
              />
            </div>
          </div>

          <!-- Total Price Card -->
          <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);display:flex;align-items:center;justify-content:space-between;">
            <div>
              <div style="font-size:13px;color:#666;font-weight:500;">${currentUiText.totalPriceLabel}</div>
              <div style="font-size:11px;color:#888;">${lang === 'th' ? 'ชำระเงินที่ร้านเมื่อเข้ารับบริการ' : lang === 'zh' ? '到店支付' : lang === 'ru' ? 'Оплата на месте' : lang === 'ja' ? '現地でお支払い' : lang === 'ko' ? '현장 결제' : 'Pay at Venue'}</div>
            </div>
            <div id="spa-total-price-display" style="font-size:22px;font-weight:700;color:#00B900;">฿${totalPrice.toLocaleString()}</div>
          </div>
        </div>
      </div>

      <!-- Version Footer -->

      ${AppVersionFooter()}

    </div>

    ${BackToTopButton('spa')}

    ${FloatingNav()}
  `;
}

function updateSpaPriceDisplay(): void {
  const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
  const currentTreatments = activeOutlet.treatments || TREATMENTS;
  const treat = currentTreatments.find(t => t.code === selectedTreatmentCode) || currentTreatments[0] || TREATMENTS[0];
  const totalPrice = (treat.price || 1000) * paxCount;
  const priceEl = document.getElementById('spa-total-price-display');
  if (priceEl) {
    priceEl.textContent = `฿${totalPrice.toLocaleString()}`;
  }
}

export function initSpaPage(): void {
  // Only fetch config from network once on initial entry
  if (!spaConfigLoaded) {
    getSpaConfig().then((cfg: any) => {
      spaConfigLoaded = true;
      if (cfg) {
        const editDataStr = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingData') : null;
        let editOutletId: string | null = null;
        if (editDataStr) {
          try {
            const editBooking = JSON.parse(editDataStr);
            const spa = editBooking.spaDetails || editBooking.customFields?.spaDetails;
            if (spa?.outletId) editOutletId = spa.outletId;
          } catch (_) {}
        }

        if (cfg.outlets && cfg.outlets.length > 0) {
          spaOutlets = cfg.outlets;
          if (editOutletId && spaOutlets.some(o => o.id === editOutletId)) {
            activeOutletId = editOutletId;
          } else if (!activeOutletId || !spaOutlets.some(o => o.id === activeOutletId)) {
            activeOutletId = spaOutlets[0].id || null;
          }
        } else if (cfg.id) {
          spaOutlets = [cfg];
          activeOutletId = cfg.id || 'spa_main';
        } else {
          spaOutlets = [];
          activeOutletId = null;
        }
        const active = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0];
        if (active && active.treatments && active.treatments.length > 0) {
          TREATMENTS = active.treatments;
          if (!TREATMENTS.some(t => t.code === selectedTreatmentCode)) {
            selectedTreatmentCode = TREATMENTS[0].code;
          }
        }
        (window as any).renderApp?.();
      }
    }).catch(err => {
      console.error('[SpaPage] Error fetching dynamic config:', err);
      spaConfigLoaded = true;
    });
  }

  // Render Carousel for Spa Outlet Images
  const editDataStr = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingData') : null;
  if (editDataStr) {
    try {
      const editBooking = JSON.parse(editDataStr);
      const spa = editBooking.spaDetails || editBooking.customFields?.spaDetails;
      if (spa) {
        if (spa.bookingDate) selectedDateStr = spa.bookingDate;
        if (spa.timeSlot) selectedTimeSlot = spa.timeSlot;
        if (spa.treatmentCode) selectedTreatmentCode = spa.treatmentCode;
        if (spa.pax) paxCount = spa.pax;
        if (spa.roomNumber) roomNumberInput = spa.roomNumber;
        if (spa.outletId && spaOutlets.some(o => o.id === spa.outletId)) activeOutletId = spa.outletId;
      }
    } catch (_) {}
  }

  const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
  const outletImages = activeOutlet.images || [];
  const imagesContainer = document.getElementById('spa-images-container');
  if (imagesContainer && outletImages.length > 0) {
    renderCarousel(imagesContainer, outletImages);
  }

  (window as any).selectSpaOutlet = (outletId: string) => {
    if (activeOutletId === outletId) return;
    activeOutletId = outletId;
    const active = spaOutlets.find(o => o.id === outletId);
    if (active && active.treatments && active.treatments.length > 0) {
      TREATMENTS = active.treatments;
      if (!TREATMENTS.some(t => t.code === selectedTreatmentCode)) {
        selectedTreatmentCode = TREATMENTS[0].code;
      }
    }
    const savedY = window.scrollY;
    (window as any).renderApp?.();
    window.scrollTo({ top: savedY, behavior: 'instant' as any });

    // Smooth scroll inside horizontal tab container only (no window scroll)
    const tabEl = document.getElementById(`spa-outlet-tab-${outletId}`);
    const scrollContainer = document.getElementById('spa-outlets-scroll');
    if (tabEl && scrollContainer) {
      const offsetLeft = tabEl.offsetLeft - (scrollContainer.clientWidth / 2) + (tabEl.clientWidth / 2);
      scrollContainer.scrollTo({ left: offsetLeft, behavior: 'smooth' });
    }
  };


  // Initialize Litepicker single-date calendar for Spa
  const spaPickerContainer = document.getElementById('spa-litepicker-container');
  const spaDateTrigger = document.getElementById('spa-date-range-trigger');
  const spaDateDisplay = document.getElementById('spa-date-display');
  const spaDateToggleIcon = document.getElementById('spa-date-toggle-icon');
  const lang = getCurrentLanguage();

  const litepickerLangMap: Record<string, string> = {
    th: 'th-TH',
    en: 'en-US',
    zh: 'zh-CN',
    ru: 'ru-RU',
    ja: 'ja-JP',
    ko: 'ko-KR',
  };

  if (spaPickerContainer && spaDateTrigger) {
    spaDateTrigger.addEventListener('click', () => {
      spaPickerContainer.classList.toggle('collapsed');
      if (spaDateToggleIcon) {
        spaDateToggleIcon.style.transform = spaPickerContainer.classList.contains('collapsed') ? '' : 'rotate(180deg)';
      }
    });

    const spaPicker = new Litepicker({
      element: spaPickerContainer,
      inlineMode: true,
      singleMode: true,
      numberOfMonths: 1,
      numberOfColumns: 1,
      firstDay: 1,
      minDate: new Date(),
      startDate: selectedDateStr,
      format: 'YYYY-MM-DD',
      lang: litepickerLangMap[lang] || 'en-US',
      buttonText: {
        previousMonth: '‹',
        nextMonth: '›',
        apply: 'Apply',
        cancel: 'Cancel',
        reset: 'Reset',
      },
      plugins: ['mobilefriendly'],
      mobileFriendly: true,
      setup: picker => {
        picker.on('selected', (start) => {
          if (start) {
            const iso = start.format('YYYY-MM-DD');
            selectedDateStr = iso;

            // Update display
            if (spaDateDisplay) {
              try {
                const d = new Date(iso + 'T00:00:00');
                spaDateDisplay.textContent = d.toLocaleDateString(
                  litepickerLangMap[lang] || 'en-US',
                  { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
                );
              } catch (_) {
                spaDateDisplay.textContent = iso;
              }
            }

            // Update hidden input
            const hiddenInput = document.getElementById('spa-date-hidden') as HTMLInputElement;
            if (hiddenInput) hiddenInput.value = iso;

            // Auto collapse after selection
            setTimeout(() => {
              spaPickerContainer.classList.add('collapsed');
              if (spaDateToggleIcon) spaDateToggleIcon.style.transform = '';
            }, 300);
          }
        });
      },
    });

    (window as any).__spaLitepicker = spaPicker;
  }


  // Direct In-Place DOM Update for Time Slot (No page re-render, zero scroll jump)
  (window as any).selectSpaTimeSlot = (slot: string) => {
    if (selectedTimeSlot === slot) return;
    selectedTimeSlot = slot;
    document.querySelectorAll('.spa-time-chip').forEach((el: any) => {
      el.style.border = '1px solid #e0e0e0';
      el.style.background = '#fff';
      el.style.color = '#333';
    });
    const activeEl = document.getElementById(`spa-time-chip-${slot.replace(':', '_')}`);
    if (activeEl) {
      activeEl.style.border = '1px solid #00B900';
      activeEl.style.background = '#00B900';
      activeEl.style.color = '#fff';
    }
  };

  // Direct In-Place DOM Update for Treatment (No page re-render, zero scroll jump)
  (window as any).selectSpaTreatment = (code: string) => {
    if (selectedTreatmentCode === code) return;
    selectedTreatmentCode = code;
    
    document.querySelectorAll('.spa-treat-item').forEach((el: any) => {
      const cardCode = el.getAttribute('data-code');
      const isCur = cardCode === code;
      el.style.border = isCur ? '1.5px solid #00B900' : '1.5px solid #e0e0e0';
      el.style.background = isCur ? '#F9FDF9' : '#fff';
      const radio = el.querySelector('input[type=radio]') as HTMLInputElement;
      if (radio) radio.checked = isCur;
    });

    updateSpaPriceDisplay();
  };

  // Direct In-Place DOM Update for Pax (No page re-render, zero scroll jump)
  (window as any).changeSpaPax = (delta: number) => {
    const newPax = Math.max(1, Math.min(10, paxCount + delta));
    if (newPax === paxCount) return;
    paxCount = newPax;
    
    const paxEl = document.getElementById('spa-pax-display');
    if (paxEl) paxEl.textContent = String(paxCount);

    updateSpaPriceDisplay();
  };

  (window as any).updateSpaRoomNumber = (val: string) => {
    roomNumberInput = val;
  };

  (window as any).proceedSpaToGuestInfo = () => {
    const lang = getCurrentLanguage();
    const inputEl = document.getElementById('spa_room_number') as HTMLInputElement;
    if (inputEl) roomNumberInput = inputEl.value;

    const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
    const currentTreatments = activeOutlet.treatments || TREATMENTS;
    const treat = currentTreatments.find(t => t.code === selectedTreatmentCode) || currentTreatments[0] || TREATMENTS[0];
    const totalPrice = (treat.price || 1000) * paxCount;
    const treatNameObj = typeof treat.name === 'object'
      ? treat.name
      : { th: treat.nameTh || treat.name, en: treat.nameEn || treat.name, zh: treat.nameZh || treat.name };

    setState({
      bookingType: 'SPA',
      spaDetails: {
        outletId: activeOutlet.id || 'spa-outlet',
        outletName: activeOutlet.name || { th: 'สปาและทรีตเมนต์', en: 'Spa & Wellness', zh: '水疗中心' },
        treatmentCode: treat.code,
        treatmentName: treatNameObj,
        bookingDate: selectedDateStr,
        timeSlot: selectedTimeSlot,
        durationMinutes: treat.duration,
        pax: paxCount,
        roomNumber: roomNumberInput,
        inHouseGuestName: roomNumberInput ? `ห้อง ${roomNumberInput}` : undefined,
      },
      selectedRoom: {
        roomTypeCode: treat.code,
        roomTypeName: `🌿 ${getLocalizedText(treatNameObj, lang, treat.code)}`,
        ratePlanCode: 'PAY_AT_VENUE',
        ratePlanName: 'ชำระเงินที่ร้าน (Pay at Spa)',
        totalRate: totalPrice,
        ratePerRoom: totalPrice,
        averageNightlyRate: totalPrice,
        totalBeforeTax: Math.round(totalPrice / 1.177),
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
        description: `รอบเวลา ${selectedTimeSlot} น. สำหรับ ${paxCount} ท่าน`,
      },
    });

    navigateTo('guest-info');
  };

  window.handleForward = () => {
    (window as any).proceedSpaToGuestInfo?.();
  };

  window.handleBack = () => {
    navigateTo('search');
  };

  // Floating "BACK TO TOP" for Spa
  initBackToTop('spa');
}
